import { Link } from "react-router-dom";
import { PublicNav } from "@/components/PublicNav";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />
      <div className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="text-3xl font-heading font-semibold tracking-tight mb-2">
          Terms of Service
        </h1>
        <p className="text-muted-foreground mb-8">
          Last updated: March 13, 2026
        </p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Agreement to Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using the Distilled Intuition platform (&quot;Service&quot;) operated by Distilled
              Intuition Inc. (&quot;Company,&quot; &quot;we,&quot; &quot;us&quot;), you agree to be bound by these Terms of Service
              (&quot;Terms&quot;). If you are using the Service on behalf of an organization, you represent that
              you have authority to bind that organization. If you do not agree to these Terms, do not use
              the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Distilled Intuition is an AI-native learning management platform that enables organizations
              and individuals to create, deliver, and manage training courses. The Service includes web
              and mobile applications, AI-powered content generation, skill trees, assessments, analytics,
              and related features.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Account Registration and Security</h2>
            <p className="text-muted-foreground">
              You must provide accurate and complete information when creating an account. You are
              responsible for maintaining the confidentiality of your credentials and for all activities
              under your account. Notify us immediately of any unauthorized access. We reserve the right to
              suspend or terminate accounts that violate these Terms or that we reasonably believe pose a
              security risk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Acceptable Use</h2>
            <p className="text-muted-foreground mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Use the Service for any illegal purpose or in violation of applicable laws.</li>
              <li>Upload or transmit content that infringes intellectual property, is defamatory, or
                otherwise unlawful.</li>
              <li>Attempt to gain unauthorized access to the Service, other accounts, or our systems.</li>
              <li>Interfere with or disrupt the Service or its infrastructure.</li>
              <li>Use the Service to distribute malware, spam, or harmful content.</li>
              <li>Resell or sublicense the Service without our prior written consent.</li>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Service.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We may remove content or suspend accounts that violate these policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Your Content and IP</h2>
            <p className="text-muted-foreground">
              You retain ownership of content you create or upload (&quot;Your Content&quot;). By using the Service,
              you grant us a worldwide, non-exclusive, royalty-free license to use, store, process, and
              display Your Content as needed to provide and improve the Service. You represent that you
              have all rights necessary to grant this license and that Your Content does not infringe
              third-party rights. AI-generated content produced through the Service for your use remains
              available to you subject to your subscription.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Subscription and Payment</h2>
            <p className="text-muted-foreground">
              Paid plans are subject to the pricing and billing terms presented at signup or in your
              subscription agreement. Fees are typically charged in advance (e.g., monthly or annually).
              You may cancel your subscription according to the terms of your plan. Refunds are handled
              according to our refund policy. We may change pricing with reasonable notice; continued use
              after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Free Tier and Trials</h2>
            <p className="text-muted-foreground">
              We may offer free tiers or trial periods. Free tiers may have usage limits, feature
              restrictions, or shorter data retention. Trials may convert to paid subscriptions at the end
              of the trial unless cancelled. Specific terms for free or trial access are disclosed at
              enrollment.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Service Availability</h2>
            <p className="text-muted-foreground">
              We strive to maintain high availability but do not guarantee uninterrupted access. We may
              perform maintenance, updates, or modifications that temporarily affect availability. We are
              not liable for downtime except as expressly provided in a service level agreement (SLA).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Disclaimers</h2>
            <p className="text-muted-foreground">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE
              WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED. AI-GENERATED CONTENT MAY CONTAIN INACCURACIES;
              YOU ARE RESPONSIBLE FOR REVIEWING AND VERIFYING SUCH CONTENT BEFORE USE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, DISTILLED INTUITION INC. AND ITS AFFILIATES,
              OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM
              YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE
              TWELVE (12) MONTHS PRECEDING THE CLAIM. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS;
              IN SUCH CASES, OUR LIABILITY IS LIMITED TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Indemnification</h2>
            <p className="text-muted-foreground">
              You agree to indemnify and hold harmless Distilled Intuition Inc. and its affiliates from
              any claims, damages, losses, or expenses (including reasonable attorneys&apos; fees) arising from
              your use of the Service, Your Content, or your violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Termination</h2>
            <p className="text-muted-foreground">
              You may stop using the Service at any time. We may suspend or terminate your access
              immediately for breach of these Terms or for other reasons we deem appropriate. Upon
              termination, your right to use the Service ceases. We may retain data as required by law or
              our data retention policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may modify these Terms at any time. We will notify you of material changes by posting
              the updated Terms on this page and updating the &quot;Last updated&quot; date. Continued use of
              the Service after changes constitutes acceptance. If you do not agree, you must discontinue
              use.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. General</h2>
            <p className="text-muted-foreground mb-3">
              These Terms constitute the entire agreement between you and Distilled Intuition Inc.
              regarding the Service. Any waiver must be in writing. If any provision is found
              unenforceable, the remaining provisions remain in effect. These Terms are governed by the
              laws of the State of Delaware, without regard to conflict of law principles. Disputes shall
              be resolved in the courts of Delaware or, where appropriate, through binding arbitration.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">15. Contact</h2>
            <p className="text-muted-foreground">
              For questions about these Terms, contact us at:{" "}
              <a href="mailto:legal@distilledintuition.training" className="text-primary hover:underline">
                legal@distilledintuition.training
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t">
          <Link to="/" className="text-primary hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
